import { useMemo, useRef, useState, type ClipboardEvent, type KeyboardEvent, type ReactNode } from "react";
import { X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { PanelFieldLabel } from "@/components/producer/panel";

export function CellOptionsInput({
  label,
  value,
  onChange,
  placeholder = "Digite uma opção e pressione Enter...",
  helperText,
}: {
  label: string;
  value: string;
  onChange: (newValue: string) => void;
  placeholder?: string;
  helperText?: ReactNode;
}) {
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const options = useMemo(() => {
    if (!value) return [];
    return value.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
  }, [value]);

  const updateOptions = (nextOptions: string[]) => {
    onChange(nextOptions.join("\n"));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const trimmed = inputValue.trim();
      if (trimmed) {
        if (!options.includes(trimmed)) {
          updateOptions([...options, trimmed]);
        }
        setInputValue("");
      }
    } else if (e.key === "Backspace" && !inputValue && options.length > 0) {
      e.preventDefault();
      updateOptions(options.slice(0, -1));
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text") || "";
    const items = pastedText
      .split(/[\r\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);

    if (items.length > 0) {
      const next = [...options];
      for (const item of items) {
        if (!next.includes(item)) {
          next.push(item);
        }
      }
      updateOptions(next);
      setInputValue("");
    }
  };

  const removeOption = (indexToRemove: number) => {
    updateOptions(options.filter((_, idx) => idx !== indexToRemove));
  };

  const clearAll = () => {
    updateOptions([]);
    setInputValue("");
  };

  const handleBlur = () => {
    const trimmed = inputValue.trim();
    if (trimmed) {
      if (!options.includes(trimmed)) {
        updateOptions([...options, trimmed]);
      }
      setInputValue("");
    }
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <PanelFieldLabel>{label}</PanelFieldLabel>
        {options.length > 0 && (
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-[10px] font-medium bg-muted text-muted-foreground border-0">
              {options.length} {options.length === 1 ? "item" : "itens"}
            </Badge>
            <button
              type="button"
              onClick={clearAll}
              className="text-[11px] text-muted-foreground/70 hover:text-foreground transition-colors"
            >
              Limpar todos
            </button>
          </div>
        )}
      </div>

      <div
        onClick={() => inputRef.current?.focus({ preventScroll: true })}
        className="mt-2 flex min-h-[84px] max-h-40 cursor-text flex-wrap items-center gap-2 overflow-y-auto rounded-2xl bg-secondary/50 p-2.5 transition-[background-color,box-shadow] duration-150 ease-out focus-within:bg-background focus-within:ring-1 focus-within:ring-primary/30"
      >
        {options.map((option, idx) => (
          <div
            key={idx}
            className="rounded-full bg-muted/80 text-foreground border border-border/50 pl-3 pr-1.5 py-1 text-xs font-medium flex items-center gap-1.5 shadow-2xs group hover:bg-muted transition-all active:scale-[0.98] animate-in fade-in zoom-in-95 duration-100"
          >
            <span>{option}</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeOption(idx);
              }}
              className="text-muted-foreground/60 hover:text-foreground hover:bg-background/80 rounded-full p-0.5 transition-colors"
              title="Remover"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}

        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={handleBlur}
          placeholder={options.length === 0 ? placeholder : "+ Adicionar..."}
          className="bg-transparent text-xs text-foreground placeholder:text-muted-foreground/50 border-0 outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0 shadow-none py-1 px-1.5 min-w-[90px] w-auto flex-1 h-7"
        />
      </div>

      <p className="text-[11px] text-muted-foreground">
        {helperText || (
          <>
            Pressione <kbd className="px-1.5 py-0.5 bg-muted rounded border text-[10px] font-mono">Enter</kbd> para separar. Você também pode colar colunas do Excel.
          </>
        )}
      </p>
    </div>
  );
}
